// Organization Health as a dependency graph.
//
// The list below this component is the same data read as a roster; this is the
// same data read as a shape. Nothing here is invented: every node, its status,
// every edge and — the part that makes it worth drawing — every edge's
// `evidence` string come from `GET /api/organization/health-topology`.
//
// WHY A COLUMN AND NOT A RING (V2PORT-WS1, Founder-approved 2026-08-28)
// =====================================================================
// This replaced concentric rings. Rings were stable and learnable, which was
// the right instinct, but they put every subsection at a different radius and
// left the reader deriving the grouping from distance. The approved layout
// states it instead: one core, a tier of subsections generated from the node
// `type` field, and each subsection's systems in a column beside it.
//
// The property that made rings worth keeping is preserved and is the reason
// `layout` is still a pure function of the payload: nodes are grouped by type
// in a FIXED order and sorted by id inside each group, so a node stays put
// until the topology itself changes. Nothing here simulates or randomises.
//
// Structural edges (core -> subsection -> system) cannot cross, because the
// groups are contiguous and the column is monotonic. Dependency edges cross by
// nature, so they are drawn only for the focused node — see below.
//
// HOVER IS THE FEATURE. The endpoint's own doctrine is that an edge exists
// only where the codebase can prove it — "39 of 49 scheduled jobs, and
// integrations with no scheduled job behind them, have no evidenced edge and
// appear as unconnected nodes rather than a guessed relationship". So focusing
// a node dims everything off its chain and shows the evidence for each edge
// verbatim. An unconnected node is information, not a gap, and is drawn hollow.
import React, { useMemo, useState } from "react";

import "./health-graph.css";

// The middle tier. Order is fixed so the column is stable; a `type` the API
// starts returning that is not listed here still renders, in its own group at
// the end, rather than vanishing from a graph that claims to be complete.
export const SUBSECTIONS = [
  { type: "business_unit", label: "BUSINESS UNITS" },
  { type: "integration", label: "INTEGRATIONS" },
  { type: "scheduler_job", label: "SCHEDULED JOBS" },
  { type: "system", label: "SYSTEM" },
];

const CORE_X = 172;
const HUB_X = 552;
const LEAF_X = 838;
const W = 1180;
const GAP = 28.5;        // between systems in a column
const GROUP_GAP = 26;    // extra between subsections
const TOP = 46;
const MIN_H = 560;

const DOT = { system: 6, business_unit: 6, integration: 5.5, scheduler_job: 5 };

/** The subsection order for a payload, including any type the API added. */
export function subsectionsFor(nodes) {
  const known = SUBSECTIONS.filter((s) => (nodes || []).some((n) => n.type === s.type));
  const extra = [...new Set((nodes || [])
    .map((n) => n.type || "unknown")
    .filter((t) => !SUBSECTIONS.some((s) => s.type === t)))]
    .sort()
    .map((t) => ({ type: t, label: String(t).replace(/_/g, " ").toUpperCase() }));
  return [...known, ...extra];
}

/**
 * Place every node in its subsection's column. Pure, deterministic and sorted
 * by id, so a node never moves between loads.
 */
export function layout(nodes) {
  const list = nodes || [];
  if (!list.length) return [];
  const out = [];
  let y = TOP;
  for (const sub of subsectionsFor(list)) {
    const group = list
      .filter((n) => (n.type || "unknown") === sub.type)
      .slice()
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
    group.forEach((n, i) => {
      out.push({ ...n, x: LEAF_X, y: y + i * GAP, sub });
    });
    y += (group.length - 1) * GAP + GAP + GROUP_GAP;
  }
  return out;
}

/** Where each subsection's hub sits: the centroid of its own systems. */
export function hubsFor(placed) {
  const seen = [];
  for (const p of placed) {
    let hub = seen.find((h) => h.type === p.sub.type);
    if (!hub) {
      hub = { type: p.sub.type, label: p.sub.label, x: HUB_X, ys: [], count: 0 };
      seen.push(hub);
    }
    hub.ys.push(p.y);
    hub.count += 1;
  }
  return seen.map((h) => ({ ...h, y: (h.ys[0] + h.ys[h.ys.length - 1]) / 2 }));
}

function canvasHeight(placed) {
  if (!placed.length) return MIN_H;
  return Math.max(MIN_H, placed[placed.length - 1].y + TOP);
}

const COLOUR = {
  green: "var(--oh-good)",
  yellow: "var(--oh-warn)",
  red: "var(--oh-crit)",
  unknown: "var(--oh-dim)",
};
const ACCENT = {
  business_unit: "var(--oh-a-bu)",
  integration: "var(--oh-a-int)",
  scheduler_job: "var(--oh-a-job)",
  system: "var(--oh-a-sys)",
};
const accentOf = (t) => ACCENT[t] || "var(--oh-a-sys)";

/** An S-curve that leaves and arrives horizontally, so a lead-in stays tangent. */
function curve(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  return `M${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`;
}
/** Dependency edges bow left, into the space the structural tier leaves free. */
function arc(x1, y1, x2, y2) {
  const bow = Math.min(250, 70 + Math.abs(y2 - y1) * 0.6);
  return `M${x1} ${y1} C ${x1 - bow} ${y1} ${x2 - bow} ${y2} ${x2} ${y2}`;
}

/* ── the core ──────────────────────────────────────────────────────────────
 * A dimensional object rather than a filled circle, built only from gradients
 * and geometry — no raster asset, no SVG filter. Depth comes from an off-centre
 * radial gradient (the body shades, the nucleus lights), an atmosphere above it
 * and a bloom that reaches past the boundary so the branches emerge from inside
 * the field instead of clipping to an edge. Motion is calm by design: the
 * outer geometry drifts, the volume breathes ~1%, and `prefers-reduced-motion`
 * stops all of it. */
function CoreDefs({ y }) {
  return (
    <defs>
      <radialGradient id="ohBloom">
        <stop offset="0" stopColor="#A6F6FF" stopOpacity=".34" />
        <stop offset=".22" stopColor="#4FE0F5" stopOpacity=".2" />
        <stop offset=".44" stopColor="#2FA9D8" stopOpacity=".11" />
        <stop offset=".68" stopColor="#7A6BDD" stopOpacity=".055" />
        <stop offset=".86" stopColor="#8B62D8" stopOpacity=".022" />
        <stop offset="1" stopColor="#8B62D8" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="ohSphere" fx=".38" fy=".30" r=".78">
        <stop offset="0" stopColor="#2C8CAF" />
        <stop offset=".09" stopColor="#237E9F" />
        <stop offset=".20" stopColor="#155F7D" />
        <stop offset=".36" stopColor="#0B3A4D" />
        <stop offset=".55" stopColor="#072736" />
        <stop offset=".80" stopColor="#061823" />
        <stop offset="1" stopColor="#0A1018" />
      </radialGradient>
      <radialGradient id="ohShell">
        <stop offset="0" stopColor="#39E7F2" stopOpacity="0" />
        <stop offset=".60" stopColor="#39E7F2" stopOpacity="0" />
        <stop offset=".80" stopColor="#5FD8EE" stopOpacity=".055" />
        <stop offset=".90" stopColor="#7DEEFB" stopOpacity=".105" />
        <stop offset=".96" stopColor="#A683EE" stopOpacity=".075" />
        <stop offset="1" stopColor="#A683EE" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="ohGlass" x1="0" y1="0" x2=".85" y2="1">
        <stop offset="0" stopColor="#EAFDFF" stopOpacity=".72" />
        <stop offset=".30" stopColor="#7DEEFB" stopOpacity=".2" />
        <stop offset=".62" stopColor="#39E7F2" stopOpacity=".03" />
        <stop offset="1" stopColor="#B98BF5" stopOpacity=".16" />
      </linearGradient>
      <radialGradient id="ohNucleus">
        <stop offset="0" stopColor="#FFFFFF" stopOpacity=".92" />
        <stop offset=".18" stopColor="#DEFAFF" stopOpacity=".62" />
        <stop offset=".44" stopColor="#6FE6FA" stopOpacity=".3" />
        <stop offset=".74" stopColor="#39E7F2" stopOpacity=".08" />
        <stop offset="1" stopColor="#39E7F2" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="ohHexTop" x1="0" y1="0" x2=".4" y2="1">
        <stop offset="0" stopColor="#FFFFFF" /><stop offset="1" stopColor="#9EEFFC" />
      </linearGradient>
      <linearGradient id="ohHexR" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#4FD2EC" /><stop offset="1" stopColor="#1A6E92" />
      </linearGradient>
      <linearGradient id="ohHexL" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#2E9CC4" /><stop offset="1" stopColor="#0C3B52" />
      </linearGradient>
      <clipPath id="ohInner"><circle cx={CORE_X} cy={y} r="84" /></clipPath>
    </defs>
  );
}

function Core({ y }) {
  const X = CORE_X;
  const P = (r, a) => [X + r * Math.cos(a), y + r * Math.sin(a)];

  const filaments = [];
  for (let i = 0; i < 30; i += 1) {
    const a = (i / 30) * Math.PI * 2 + 0.11;
    const [x1, y1] = P(21 + (i % 3) * 6, a);
    const [x2, y2] = P(58 + (i % 5) * 12, a);
    filaments.push(
      <line key={`f${i}`} x1={x1.toFixed(1)} y1={y1.toFixed(1)}
            x2={x2.toFixed(1)} y2={y2.toFixed(1)} stroke="#8FF0FF"
            strokeWidth={i % 4 === 0 ? 1 : 0.6} opacity={i % 3 === 0 ? 0.46 : 0.22} />
    );
    if (i % 5 === 0) {
      filaments.push(
        <circle key={`n${i}`} cx={x2.toFixed(1)} cy={y2.toFixed(1)} r="1.6"
                fill="#BFF6FF" opacity=".6" />
      );
    }
  }
  [34, 52, 70].forEach((r, k) =>
    filaments.push(
      <circle key={`c${r}`} className="oh-circ" style={{ "--d": k }} cx={X} cy={y} r={r}
              fill="none" stroke="#7DEEFB" strokeWidth=".7"
              opacity={0.26 - k * 0.05} strokeDasharray={`${3 + k * 2} ${9 + k * 4}`} />
    )
  );

  // the hexagon, as three lit isometric facets rather than a thin outline
  const h = [[0, -26], [24, -12], [24, 12], [0, 26], [-24, 12], [-24, -12]]
    .map(([a, b]) => [X + a, y + b]);
  const poly = (pts) => pts.map((p) => p.join(",")).join(" ");

  return (
    <g className="oh-core" aria-hidden="true">
      <circle className="oh-bloom" cx={X} cy={y} r="188" fill="url(#ohBloom)" />
      <circle className="oh-tick" cx={X} cy={y} r="146" fill="none" stroke="#7DEEFB"
              strokeWidth="1" strokeDasharray="1.5 13" opacity=".3" />
      <circle className="oh-halo" cx={X} cy={y} r="118" fill="none" />
      <circle className="oh-shell" cx={X} cy={y} r="103" fill="url(#ohShell)" />
      <g className="oh-body">
        <circle cx={X} cy={y} r="84" fill="url(#ohSphere)" />
        <g clipPath="url(#ohInner)" className="oh-fil">{filaments}</g>
        <circle cx={X} cy={y} r="84" fill="none" stroke="url(#ohGlass)" strokeWidth="1.6" />
        <circle cx={X} cy={y} r="79" fill="none" stroke="#EAFDFF" strokeWidth=".7"
                opacity=".16" strokeDasharray="96 320"
                transform={`rotate(-128 ${X} ${y})`} />
        <circle className="oh-nuc" cx={X} cy={y} r="38" fill="url(#ohNucleus)" />
        <g className="oh-glyph">
          <polygon points={poly([h[5], h[0], h[1], [X, y]])} fill="url(#ohHexTop)" opacity=".95" />
          <polygon points={poly([h[1], h[2], h[3], [X, y]])} fill="url(#ohHexR)" opacity=".8" />
          <polygon points={poly([h[3], h[4], h[5], [X, y]])} fill="url(#ohHexL)" opacity=".78" />
          <polygon points={poly(h)} fill="none" stroke="#EAFDFF" strokeWidth="1.3"
                   strokeLinejoin="round" opacity=".9" />
          <path d={`M${h[5][0]} ${h[5][1]} L${X} ${y} L${h[1][0]} ${h[1][1]}`} fill="none"
                stroke="#EAFDFF" strokeWidth=".8" opacity=".5" />
        </g>
      </g>
      <text className="oh-coret" x={X} y={y + 118} textAnchor="middle">CIPHER CORE</text>
      <text className="oh-cores" x={X} y={y + 138} textAnchor="middle">ORGANIZATION HEALTH</text>
    </g>
  );
}

export default function HealthGraph({ nodes, edges, coverageNote }) {
  const placed = useMemo(() => layout(nodes || []), [nodes]);
  const hubs = useMemo(() => hubsFor(placed), [placed]);
  const byId = useMemo(() => {
    const m = {};
    for (const p of placed) m[p.id] = p;
    return m;
  }, [placed]);
  const [focus, setFocus] = useState(null);

  const drawn = (edges || []).filter((e) => byId[e.from] && byId[e.to]);
  const chain = focus
    ? new Set([focus, ...drawn.filter((e) => e.from === focus || e.to === focus)
        .flatMap((e) => [e.from, e.to])])
    : null;
  const focusEdges = focus
    ? drawn.filter((e) => e.from === focus || e.to === focus)
    : [];

  // an unconnected node is a finding, not a rendering gap
  const connected = new Set(drawn.flatMap((e) => [e.from, e.to]));
  const unconnected = placed.filter((p) => !connected.has(p.id));
  const H = canvasHeight(placed);
  const coreY = H / 2;

  return (
    <div className="oh-graphwrap">
      <div className={"oh-graph" + (focus ? " oh-focused" : "")}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img"
             aria-label="Dependency graph across business units, integrations, scheduled jobs and the circuit breaker.">
          <CoreDefs y={coreY} />

          {/* structural tier: core -> subsection -> system. Never crosses. */}
          <g className="oh-struct-g">
            {hubs.map((hub, i) => {
              // the curve is extended inward by a horizontal lead-in, which is
              // tangent-continuous, so branches begin inside the core's field
              const d = `M${CORE_X + 30} ${coreY} L`
                + curve(CORE_X + 78, coreY, hub.x - 30, hub.y).slice(1);
              return (
                <g key={hub.type}>
                  <path className="oh-struct" d={d} stroke={accentOf(hub.type)} />
                  <path className="oh-spark" style={{ "--i": i }} d={d}
                        stroke={accentOf(hub.type)} />
                </g>
              );
            })}
            {placed.map((n, i) => {
              const hub = hubs.find((h) => h.type === n.sub.type);
              const d = curve(hub.x + 30, hub.y, n.x - 9, n.y);
              return (
                <g key={`s-${n.id}`}>
                  <path className="oh-struct" d={d} stroke={accentOf(n.type)} />
                  <path className="oh-spark" style={{ "--i": i * 0.37 }} d={d}
                        stroke={accentOf(n.type)} />
                </g>
              );
            })}
          </g>

          {/* dependency tier: real edges only, revealed for the focused node */}
          {drawn.map((e, i) => {
            const a = byId[e.from];
            const b = byId[e.to];
            const on = focus && (e.from === focus || e.to === focus);
            return (
              <path key={`${e.from}->${e.to}-${i}`}
                    className={"oh-edge" + (on ? " on" : "")}
                    d={arc(a.x - 9, a.y, b.x - 9, b.y)} />
            );
          })}

          <Core y={coreY} />

          {hubs.map((hub) => (
            <g key={`h-${hub.type}`} className="oh-hub" aria-hidden="true">
              <circle className="oh-hring" cx={hub.x} cy={hub.y} r="29"
                      stroke={accentOf(hub.type)} />
              <circle className="oh-hfill" cx={hub.x} cy={hub.y} r="24"
                      fill={accentOf(hub.type)} />
              <text className="oh-hn" x={hub.x} y={hub.y + 5} textAnchor="middle">{hub.count}</text>
              <text className="oh-hl" x={hub.x} y={hub.y + 48} textAnchor="middle"
                    fill={accentOf(hub.type)}>{hub.label}</text>
            </g>
          ))}

          {placed.map((n) => {
            const rel = chain ? chain.has(n.id) : true;
            const lone = !connected.has(n.id);
            const c = COLOUR[n.status] || COLOUR.unknown;
            return (
              <g key={n.id}
                 className={"oh-node-g" + (focus === n.id ? " on" : "") + (rel ? " rel" : "")
                            + (lone ? " lone" : "")}
                 tabIndex={0} role="button"
                 aria-label={`${n.label}. Status ${n.status}. Trace dependencies.`}
                 onMouseEnter={() => setFocus(n.id)}
                 onMouseLeave={() => setFocus(null)}
                 onFocus={() => setFocus(n.id)}
                 onBlur={() => setFocus(null)}>
                <circle className="oh-hit" cx={n.x} cy={n.y} r="15" fill="transparent" />
                <circle className="oh-lring" cx={n.x} cy={n.y} r={(DOT[n.type] || 5) + 3}
                        fill="none" stroke={c} />
                <circle className="oh-ldot" cx={n.x} cy={n.y} r="3.4" fill={c} />
                <text x={n.x + 20} y={n.y + 4}>{n.label}</text>
              </g>
            );
          })}
        </svg>
      </div>

      <aside className="oh-trace card">
        <h3>{focus ? byId[focus].label : "Trace a dependency"}</h3>
        <div className="oh-trace-sub mono">
          {focus ? byId[focus].id : "HOVER OR FOCUS ANY NODE"}
        </div>

        {focus && focusEdges.length ? (
          <ul className="oh-evidence">
            {focusEdges.map((e, i) => (
              <li key={i}>
                <div className="oh-ev-head mono">
                  {e.from === focus ? "→" : "←"} {byId[e.from === focus ? e.to : e.from].label}
                  <span className="oh-rel">{e.relationship}</span>
                </div>
                {/* verbatim from the API: the code that proves this edge */}
                {e.evidence ? <p className="oh-ev">{e.evidence}</p> : null}
              </li>
            ))}
          </ul>
        ) : focus ? (
          <p className="oh-note">
            No evidenced edge. This node is unconnected because the codebase cannot
            prove a relationship for it — which is the finding, not a gap in the drawing.
          </p>
        ) : (
          <p className="oh-note">{coverageNote}</p>
        )}

        {!focus && unconnected.length ? (
          <div className="oh-unconn">
            <div className="oh-trace-sub mono">UNCONNECTED · {unconnected.length}</div>
            <p className="oh-note">
              Drawn hollow, with no evidenced relationship:{" "}
              {unconnected.map((u) => u.label).join(", ")}.
            </p>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
